import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { format, getDaysInMonth, startOfMonth, isSaturday, isSunday, getYear, parseISO, getDate } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarData, DayData, ColorOption, DayColors, MonthlyStats, AppParameters } from '../types';
import { mockedHolidays } from '../mocks/holidays';

const DEFAULT_UNIDADES_POR_HORA = 160;
const DEFAULT_OPENAI_MODEL = 'gpt-3.5-turbo';

// Constantes para otimização de ocupação
const TARGET_OCCUPATION = 100; // Ocupação ideal (100%)
const OCCUPATION_TOLERANCE = 2; // Tolerância para considerar ocupação otimizada (100% ± 2%)
const OCCUPATION_HIGH_THRESHOLD = 105; // Limiar para considerar ocupação alta na IA
const OCCUPATION_LOW_THRESHOLD = 95; // Limiar para considerar ocupação baixa na IA

const DEFAULT_AI_PROMPT = `Objetivo Principal: Ajustar o calendário para que a Capacidade de produção se aproxime o máximo possível da Demanda informada para cada mês, buscando uma Ocupação próxima de 100%.

Estratégia de Ajuste:

Se a Ocupação atual do mês for superior a 100% (Demanda > Capacidade):
1. Prioridade 1: Converter Sábados (CINZA) para "Hora Extra no Sábado" (AZUL_CLARO). Esta ação tem maior impacto no aumento da capacidade.
2. Prioridade 2: Se ainda necessário, converter Dias Úteis (PRETO) para "Hora Extra em Dia Normal" (AZUL_ESCURO). Esta ação permite ajustes mais finos.

Se a Ocupação atual do mês for inferior a 100% (Demanda < Capacidade):
1. Considerar a conversão de Dias Úteis (PRETO) para "Férias" (ROSA) para reduzir a capacidade. Avaliar o impacto para não reduzir excessivamente. Para o mês de Dezembro, se Férias forem sugeridas, tentar agrupá-las continuamente no final do mês.

Restrições Gerais a Serem Observadas:
- Não alocar nenhum tipo de hora extra (AZUL_CLARO ou AZUL_ESCURO) em dias que já são "Recuperação de Perdas Produtivas" (MARROM), "Férias" (ROSA) ou Feriados (VERMELHO).
- Feriados (VERMELHO) não devem ser alterados para dias de trabalho ou hora extra.
- Limitar o número total de dias com qualquer tipo de hora extra (AZUL_CLARO + AZUL_ESCURO) a um máximo de 14 dias por mês.
- Férias (ROSA) devem ser sugeridas EXCLUSIVAMENTE para o mês de Dezembro, agrupadas continuamente no final do mês.

Considerações Adicionais para IA (quando o usuário edita este prompt para o botão "Sugestão IA"):
- O usuário pode instruir a priorizar alocação de horas extras no início ou fim do mês.
- O usuário pode instruir a focar em um período específico de datas (o cálculo de ocupação base para o mês inteiro ainda se aplica para determinar a necessidade de ajuste).

Formato da Resposta Esperado (exclusivamente para IA):
A IA DEVE retornar SOMENTE um array de objetos JSON, onde cada objeto representa um dia a ser alterado. Não inclua nenhum texto explicativo antes ou depois do array JSON.
Formato do array: [{"date": "YYYY-MM-DD", "newType": "AZUL_CLARO"}, {"date": "YYYY-MM-DD", "newType": "ROSA"}, ...]
Valores válidos para "newType" (use exatamente estas strings): PRETO, CINZA, AZUL_ESCURO, AZUL_CLARO, VERMELHO, MARROM, ROSA.
Se nenhuma alteração for sugerida para um mês, a IA deve retornar um array JSON vazio: [].`;

const calculateDayHours = (): Record<ColorOption, number> => {
  const hours = {} as Record<ColorOption, number>;
  for (const key in DayColors) {
    const colorKey = key as ColorOption;
    hours[colorKey] = DayColors[colorKey].hours;
  }
  return hours;
};

const calculateDynamicDayCapacities = (dayHours: Record<ColorOption, number>, unidadesPorHora: number): Record<ColorOption, number> => {
  const capacities = {} as Record<ColorOption, number>;
  for (const key in dayHours) {
    const colorKey = key as ColorOption;
    capacities[colorKey] = dayHours[colorKey] * unidadesPorHora;
  }
  return capacities;
};

interface CalendarContextType {
  currentYear: number;
  setCurrentYear: (year: number) => void;
  calendarData: CalendarData;
  updateDayColor: (date: Date, colorKey: ColorOption) => void;
  getInitialDayData: (date: Date) => DayData;
  monthlyStats: Record<string, MonthlyStats>;
  updateMonthlyDemand: (monthKey: string, newDemand: number) => void;
  applyAutomaticSuggestions: () => void;
  applyOpenAISuggestions: (apiKey: string, currentMonthKey?: string) => Promise<void>;
  appParams: AppParameters;
  updateAppParams: (params: Partial<AppParameters>) => void;
  getCapacityForDay: (dayData: DayData) => number;
  getAIDefaultPrompt: () => string;
  resetCalendarState: () => void;
}

const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

export const CalendarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState<CalendarData>({});
  const [monthlyStats, setMonthlyStats] = useState<Record<string, MonthlyStats>>({});
  
  const [appParams, setAppParams] = useState<AppParameters>(() => {
    const savedParams = localStorage.getItem("sim_calendar_appParams");
    let initialDayHours = calculateDayHours();
    let initialUnidadesPorHora = DEFAULT_UNIDADES_POR_HORA;

    if (savedParams) {
      const parsed = JSON.parse(savedParams);
      initialUnidadesPorHora = parsed.unidadesPorHora !== undefined ? parsed.unidadesPorHora : DEFAULT_UNIDADES_POR_HORA;
      initialDayHours = parsed.dayHours ? parsed.dayHours : initialDayHours;
      parsed.dayHours = initialDayHours;
      parsed.unidadesPorHora = initialUnidadesPorHora;
      parsed.dayCapacities = calculateDynamicDayCapacities(initialDayHours, initialUnidadesPorHora);
      if (!parsed.aiPrompt || parsed.aiPrompt.includes("Respeitar as restrições:\n3.1 Não alocar hora extra em dia de Recuperação de Perdas Produtivas")) {
        parsed.aiPrompt = DEFAULT_AI_PROMPT;
      }
      if (parsed.selectedOpenAIModel === undefined) {
        parsed.selectedOpenAIModel = DEFAULT_OPENAI_MODEL;
      }
      return parsed;
    }
    
    return {
      dayHours: initialDayHours,
      unidadesPorHora: initialUnidadesPorHora,
      dayCapacities: calculateDynamicDayCapacities(initialDayHours, initialUnidadesPorHora),
      aiPrompt: DEFAULT_AI_PROMPT,
      selectedOpenAIModel: DEFAULT_OPENAI_MODEL,
    };
  });

  const getAIDefaultPrompt = () => DEFAULT_AI_PROMPT;

  const updateAppParams = useCallback((paramsToUpdate: Partial<AppParameters>) => {
    setAppParams(prevParams => {
      const newParams = { ...prevParams, ...paramsToUpdate };
      if (paramsToUpdate.dayHours || paramsToUpdate.unidadesPorHora !== undefined) {
        const updatedDayHours = paramsToUpdate.dayHours || prevParams.dayHours;
        const updatedUnidadesPorHora = paramsToUpdate.unidadesPorHora !== undefined 
                                      ? paramsToUpdate.unidadesPorHora 
                                      : prevParams.unidadesPorHora;
        newParams.dayCapacities = calculateDynamicDayCapacities(updatedDayHours, updatedUnidadesPorHora);
      }
      localStorage.setItem("sim_calendar_appParams", JSON.stringify(newParams));
      return newParams;
    });
  }, []);

  const getCapacityForDay = useCallback((dayData: DayData): number => {
    const colorKey = Object.keys(DayColors).find(key => DayColors[key as ColorOption].background === dayData.color) as ColorOption;
    if (colorKey && appParams.dayCapacities[colorKey] !== undefined) {
      return appParams.dayCapacities[colorKey];
    }
    return 0; 
  }, [appParams.dayCapacities]);

  const getInitialDayData = useCallback((date: Date): DayData => {
    const dateString = format(date, 'yyyy-MM-dd');
    const year = getYear(date);
    const holidaysForYear = mockedHolidays[year] || [];
    const isHoliday = holidaysForYear.some(hol => hol.date === dateString);

    let colorKey: ColorOption = 'PRETO';
    if (isHoliday) {
      colorKey = 'VERMELHO';
    } else if (isSaturday(date) || isSunday(date)) {
      colorKey = 'CINZA';
    }
    return {
      date,
      color: DayColors[colorKey].background,
      type: DayColors[colorKey].meaning,
    };
  }, []);

  const calculateMonthlyStats = useCallback((year: number, month: number, currentCalendarData: CalendarData) => {
    const monthKey = format(new Date(year, month), 'yyyy-MM');
    const daysInMonth = getDaysInMonth(new Date(year, month));
    let totalCapacity = 0;

    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(year, month, i);
      const dateString = format(currentDate, 'yyyy-MM-dd');
      const dayData = currentCalendarData[dateString] || getInitialDayData(currentDate);
      totalCapacity += getCapacityForDay(dayData);
    }

    const existingDemand = monthlyStats[monthKey]?.demand;
    const demand = existingDemand !== undefined ? existingDemand : Math.floor(Math.random() * (60000 - 50000 + 1)) + 50000;
    const occupation = totalCapacity > 0 ? parseFloat(((demand / totalCapacity) * 100).toFixed(1)) : Infinity;

    setMonthlyStats(prevStats => ({
      ...prevStats,
      [monthKey]: { demand, capacity: totalCapacity, occupation },
    }));
  }, [getInitialDayData, getCapacityForDay, monthlyStats]);

  useEffect(() => {
    const newCalendarData: CalendarData = {};
    const year = currentYear;

    for (let month = 0; month < 12; month++) {
      const daysInMonth = getDaysInMonth(new Date(year, month));
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dateString = format(currentDate, 'yyyy-MM-dd');
        if (!calendarData[dateString] || getYear(parseISO(dateString)) !== year) {
            newCalendarData[dateString] = getInitialDayData(currentDate);
        }
      }
    }
    setCalendarData(prevData => ({...prevData, ...newCalendarData}));
    
    const initialStatsUpdate: Record<string, MonthlyStats> = {};
    for (let month = 0; month < 12; month++) {
        const monthKey = format(new Date(year, month), 'yyyy-MM');
        const daysInMonthFramework = getDaysInMonth(new Date(year, month));
        let monthCapacity = 0;
        for (let day = 1; day <= daysInMonthFramework; day++) {
            const currentDate = new Date(year, month, day);
            const dateString = format(currentDate, 'yyyy-MM-dd');
            const dayDt = (calendarData[dateString] && getYear(parseISO(dateString)) === year) 
                            ? calendarData[dateString] 
                            : getInitialDayData(currentDate);
            monthCapacity += getCapacityForDay(dayDt);
        }
        const demand = monthlyStats[monthKey]?.demand || Math.floor(Math.random() * (60000 - 50000 + 1)) + 50000;
        const occupation = monthCapacity > 0 ? parseFloat(((demand / monthCapacity) * 100).toFixed(1)) : Infinity;
        initialStatsUpdate[monthKey] = { demand, capacity: monthCapacity, occupation };
    }
    setMonthlyStats(prev => ({...prev, ...initialStatsUpdate }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear, getInitialDayData, getCapacityForDay]);

  useEffect(() => {
    for (let month = 0; month < 12; month++) {
      calculateMonthlyStats(currentYear, month, calendarData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarData, appParams.dayCapacities, currentYear]);


  const updateDayColor = (date: Date, colorKey: ColorOption) => {
    const dateString = format(date, 'yyyy-MM-dd');
    setCalendarData(prevData => ({
      ...prevData,
      [dateString]: {
        date,
        color: DayColors[colorKey].background,
        type: DayColors[colorKey].meaning,
      },
    }));
  };
  
  /**
   * Aplica sugestões automáticas para otimizar a ocupação do calendário
   * - Para ocupação > 100%: Aumenta capacidade com prioridade para ajuste fino
   * - Para ocupação < 100% em dezembro: Reduz capacidade com férias consecutivas no final do mês
   */
  const applyAutomaticSuggestions = () => {
    const year = currentYear;
    const newCalendarData = { ...calendarData };
    let changesMadeOverall = false; 
    const MAX_EXTRA_DAYS_PER_MONTH = 14;
    const MAX_FERIAS_DAYS_PER_MONTH = 10;

    for (let month = 0; month < 12; month++) {
      const monthKey = format(new Date(year, month), 'yyyy-MM');
      const stats = monthlyStats[monthKey];
      if (!stats || stats.capacity === undefined || stats.demand === undefined) continue;

      let currentMonthCapacity = stats.capacity;
      const demand = stats.demand;
      let extraDaysChangedThisMonth = 0;
      let feriasDaysChangedThisMonth = 0;
      const daysInMonth = getDaysInMonth(new Date(year, month));
      
      // Cenário 1: Capacidade < Demanda (Ocupação > 100%), precisa aumentar capacidade
      if (currentMonthCapacity < demand) {
        const potentialSaturdays: DayData[] = [];
        const potentialWorkdays: DayData[] = [];

        // Coleta todos os dias potenciais para alteração
        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = new Date(year, month, day);
          const dateString = format(currentDate, 'yyyy-MM-dd');
          const dayData = newCalendarData[dateString] || getInitialDayData(currentDate);
          
          if (dayData.type === DayColors.VERMELHO.meaning || 
              dayData.type === DayColors.MARROM.meaning || 
              dayData.type === DayColors.ROSA.meaning) {
            continue; // Não modificar Feriados, Recuperação ou Férias existentes
          }

          if (dayData.type === DayColors.CINZA.meaning && isSaturday(currentDate)) {
            potentialSaturdays.push(dayData);
          }
          if (dayData.type === DayColors.PRETO.meaning && !isSaturday(currentDate) && !isSunday(currentDate)) {
            potentialWorkdays.push(dayData);
          }
        }
        
        // Estratégia de otimização: Usar sábados e dias úteis de forma balanceada
        // para chegar o mais próximo possível de 100% de ocupação
        
        // Primeiro, calcula o impacto de cada tipo de alteração
        const saturdayCapacityIncrease = potentialSaturdays.length > 0 ? 
          getCapacityForDay({...potentialSaturdays[0], color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning}) - 
          getCapacityForDay(potentialSaturdays[0]) : 0;
        
        const workdayCapacityIncrease = potentialWorkdays.length > 0 ? 
          getCapacityForDay({...potentialWorkdays[0], color: DayColors.AZUL_ESCURO.background, type: DayColors.AZUL_ESCURO.meaning}) - 
          getCapacityForDay(potentialWorkdays[0]) : 0;
        
        // Calcula quanto falta para atingir a demanda
        let capacityNeeded = demand - currentMonthCapacity;
        
        // Estratégia: Usar sábados primeiro, mas verificar se não ultrapassará muito o alvo
        while (capacityNeeded > 0 && potentialSaturdays.length > 0 && extraDaysChangedThisMonth < MAX_EXTRA_DAYS_PER_MONTH) {
          const dayData = potentialSaturdays.shift()!;
          const oldCapacityDay = getCapacityForDay(dayData);
          const newDayData = { ...dayData, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning };
          const newCapacityDay = getCapacityForDay(newDayData);
          const capacityIncrease = newCapacityDay - oldCapacityDay;
          
          // Verifica se adicionar este sábado não ultrapassará muito a demanda
          // Se ultrapassar, interrompe para usar dias úteis para ajuste fino
          if (capacityNeeded - capacityIncrease < -workdayCapacityIncrease) {
            // Coloca o sábado de volta na lista para possível uso futuro
            potentialSaturdays.unshift(dayData);
            break;
          }
          
          newCalendarData[format(dayData.date, 'yyyy-MM-dd')] = newDayData;
          currentMonthCapacity += capacityIncrease;
          capacityNeeded -= capacityIncrease;
          extraDaysChangedThisMonth++;
          changesMadeOverall = true;
        }
        
        // Usa dias úteis para ajuste fino
        potentialWorkdays.sort(() => Math.random() - 0.5); // Randomiza para variar as sugestões
        while (capacityNeeded > 0 && potentialWorkdays.length > 0 && extraDaysChangedThisMonth < MAX_EXTRA_DAYS_PER_MONTH) {
          const dayData = potentialWorkdays.shift()!;
          const oldCapacityDay = getCapacityForDay(dayData);
          const newDayData = { ...dayData, color: DayColors.AZUL_ESCURO.background, type: DayColors.AZUL_ESCURO.meaning };
          const newCapacityDay = getCapacityForDay(newDayData);
          const capacityIncrease = newCapacityDay - oldCapacityDay;
          
          // Se este dia útil ultrapassar muito a demanda e ainda houver sábados disponíveis,
          // verifica se um sábado seria melhor
          if (capacityNeeded - capacityIncrease < 0 && potentialSaturdays.length > 0) {
            // Verifica se algum sábado disponível seria melhor para ajuste fino
            let bestSaturdayFit = null;
            let bestSaturdayDiff = Infinity;
            
            for (const satData of potentialSaturdays) {
              const satOldCapacity = getCapacityForDay(satData);
              const satNewCapacity = getCapacityForDay({...satData, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning});
              const satIncrease = satNewCapacity - satOldCapacity;
              const satDiff = Math.abs(capacityNeeded - satIncrease);
              
              if (satDiff < bestSaturdayDiff) {
                bestSaturdayFit = satData;
                bestSaturdayDiff = satDiff;
              }
            }
            
            // Se encontrou um sábado melhor, usa ele em vez do dia útil
            if (bestSaturdayFit && bestSaturdayDiff < Math.abs(capacityNeeded - capacityIncrease)) {
              const satOldCapacity = getCapacityForDay(bestSaturdayFit);
              const satNewDayData = { ...bestSaturdayFit, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning };
              const satNewCapacity = getCapacityForDay(satNewDayData);
              
              newCalendarData[format(bestSaturdayFit.date, 'yyyy-MM-dd')] = satNewDayData;
              currentMonthCapacity += (satNewCapacity - satOldCapacity);
              capacityNeeded -= (satNewCapacity - satOldCapacity);
              extraDaysChangedThisMonth++;
              changesMadeOverall = true;
              
              // Remove o sábado usado da lista
              potentialSaturdays.splice(potentialSaturdays.indexOf(bestSaturdayFit), 1);
              
              // Coloca o dia útil de volta na lista para possível uso futuro
              potentialWorkdays.unshift(dayData);
              continue;
            }
          }
          
          // Se não encontrou sábado melhor ou se o dia útil é adequado, usa o dia útil
          newCalendarData[format(dayData.date, 'yyyy-MM-dd')] = newDayData;
          currentMonthCapacity += capacityIncrease;
          capacityNeeded -= capacityIncrease;
          extraDaysChangedThisMonth++;
          changesMadeOverall = true;
        }
        
        // Se ainda precisar de capacidade e ainda houver sábados, usa-os
        while (capacityNeeded > 0 && potentialSaturdays.length > 0 && extraDaysChangedThisMonth < MAX_EXTRA_DAYS_PER_MONTH) {
          const dayData = potentialSaturdays.shift()!;
          const oldCapacityDay = getCapacityForDay(dayData);
          const newDayData = { ...dayData, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning };
          const newCapacityDay = getCapacityForDay(newDayData);
          
          newCalendarData[format(dayData.date, 'yyyy-MM-dd')] = newDayData;
          currentMonthCapacity += (newCapacityDay - oldCapacityDay);
          capacityNeeded -= (newCapacityDay - oldCapacityDay);
          extraDaysChangedThisMonth++;
          changesMadeOverall = true;
        }
      } 
      // Cenário 2: Capacidade > Demanda (Ocupação < 100%), precisa diminuir capacidade - APENAS PARA DEZEMBRO
      else if (currentMonthCapacity > demand && month === 11) { // month === 11 é Dezembro
        const decemberWorkdaysToFerias: DayData[] = [];
        
        // Coletar dias úteis de Dezembro, ordenados do fim para o início do mês
        for (let day = daysInMonth; day >= 1; day--) { 
          const currentDate = new Date(year, month, day);
          const dateString = format(currentDate, 'yyyy-MM-dd');
          const dayData = newCalendarData[dateString] || getInitialDayData(currentDate);
          if (dayData.type === DayColors.PRETO.meaning && 
              !isSaturday(currentDate) && !isSunday(currentDate) &&
              dayData.type !== DayColors.VERMELHO.meaning && 
              dayData.type !== DayColors.MARROM.meaning) {
            decemberWorkdaysToFerias.push(dayData); 
          }
        }

        // Tentar alocar férias consecutivas no final de Dezembro
        let consecutiveFeriasCount = 0;
        let capacityToReduce = currentMonthCapacity - demand;
        
        for (const dayData of decemberWorkdaysToFerias) { // Processa do fim do mês para o início
          if (capacityToReduce <= 0 || feriasDaysChangedThisMonth >= MAX_FERIAS_DAYS_PER_MONTH) break;
          
          const oldCapacityDay = getCapacityForDay(dayData);
          const newDayData = { ...dayData, color: DayColors.ROSA.background, type: DayColors.ROSA.meaning };
          const newCapacityDay = getCapacityForDay(newDayData); 
          const capacityReduction = oldCapacityDay - newCapacityDay;

          // Verifica se a redução não será excessiva (mantém pelo menos 98% da demanda)
          if ((currentMonthCapacity - capacityReduction) >= demand * 0.98) { 
            newCalendarData[format(dayData.date, 'yyyy-MM-dd')] = newDayData;
            currentMonthCapacity -= capacityReduction;
            capacityToReduce -= capacityReduction;
            feriasDaysChangedThisMonth++;
            consecutiveFeriasCount++;
            changesMadeOverall = true;
          } else {
            // Se não puder alocar este dia como férias (ex: reduz muito a capacidade) e já vínhamos alocando, quebra a sequência.
            if (consecutiveFeriasCount > 0) break; 
          }
        }
      }
    }

    if (changesMadeOverall) {
      setCalendarData(newCalendarData);
      alert("Sugestões automáticas aplicadas!");
    } else {
      alert("Nenhuma sugestão automática foi aplicada. O calendário já parece otimizado ou não há ações válidas no momento.");
    }
  };

  /**
   * Aplica sugestões simuladas de IA para otimizar a ocupação do calendário
   * - Busca atingir ocupação próxima a 100% em todos os meses
   * - Férias são sugeridas apenas para dezembro
   */
  const applyOpenAISuggestions = useCallback(async (apiKey: string, currentMonthKey?: string) => {
    console.log("Attempting to call OpenAI suggestion (simulated)");
    console.log("API Key:", apiKey ? "Provided" : "Missing");
    console.log("Model:", appParams.selectedOpenAIModel);

    if (!apiKey || !apiKey.startsWith("sk-")) {
      alert("Chave API da OpenAI inválida ou não fornecida. Verifique as configurações no menu superior.");
      return;
    }

    let mockedAISuggestions: { date: string; newType: ColorOption }[] = [];
    const MAX_EXTRA_DAYS_PER_MONTH_IA = 14;
    const MAX_FERIAS_DAYS_PER_MONTH_IA = 10;

    try {
      // Iterar sobre todos os meses do ano corrente para a lógica simulada da IA
      for (let month = 0; month < 12; month++) {
        const monthKey = format(new Date(currentYear, month), 'yyyy-MM');
        const stats = monthlyStats[monthKey];
        const daysInMonth = getDaysInMonth(new Date(currentYear, month));
        
        if (!stats || stats.capacity === undefined || stats.demand === undefined) continue;
        
        // Copia a lógica aprimorada do botão automático para a IA
        let currentMonthCapacity = stats.capacity;
        const demand = stats.demand;
        let extraDaysAddedThisMonth = 0;
        let feriasDaysAddedThisMonth = 0;
        
        // Cenário: Ocupação alta (> TARGET_OCCUPATION)
        if (stats.occupation > TARGET_OCCUPATION && stats.occupation !== Infinity) {
          const potentialSaturdays: DayData[] = [];
          const potentialWorkdays: DayData[] = [];

          // Coleta todos os dias potenciais para alteração
          for (let day = 1; day <= daysInMonth; day++) {
            const currentDate = new Date(currentYear, month, day);
            const dateString = format(currentDate, 'yyyy-MM-dd');
            const dayData = calendarData[dateString] || getInitialDayData(currentDate);
            
            if (dayData.type === DayColors.VERMELHO.meaning || 
                dayData.type === DayColors.MARROM.meaning || 
                dayData.type === DayColors.ROSA.meaning ||
                dayData.type === DayColors.AZUL_CLARO.meaning || 
                dayData.type === DayColors.AZUL_ESCURO.meaning) {
              continue;
            }

            if (dayData.type === DayColors.CINZA.meaning && isSaturday(currentDate)) {
              potentialSaturdays.push(dayData);
            }
            if (dayData.type === DayColors.PRETO.meaning && !isSaturday(currentDate) && !isSunday(currentDate)) {
              potentialWorkdays.push(dayData);
            }
          }
          
          // Calcula o impacto de cada tipo de alteração
          const saturdayCapacityIncrease = potentialSaturdays.length > 0 ? 
            getCapacityForDay({...potentialSaturdays[0], color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning}) - 
            getCapacityForDay(potentialSaturdays[0]) : 0;
          
          const workdayCapacityIncrease = potentialWorkdays.length > 0 ? 
            getCapacityForDay({...potentialWorkdays[0], color: DayColors.AZUL_ESCURO.background, type: DayColors.AZUL_ESCURO.meaning}) - 
            getCapacityForDay(potentialWorkdays[0]) : 0;
          
          // Calcula quanto falta para atingir a demanda
          let capacityNeeded = demand - currentMonthCapacity;
          
          // Estratégia: Usar sábados primeiro, mas verificar se não ultrapassará muito o alvo
          while (capacityNeeded > 0 && potentialSaturdays.length > 0 && extraDaysAddedThisMonth < MAX_EXTRA_DAYS_PER_MONTH_IA) {
            const dayData = potentialSaturdays.shift()!;
            const oldCapacityDay = getCapacityForDay(dayData);
            const newDayData = { ...dayData, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning };
            const newCapacityDay = getCapacityForDay(newDayData);
            const capacityIncrease = newCapacityDay - oldCapacityDay;
            
            // Verifica se adicionar este sábado não ultrapassará muito a demanda
            if (capacityNeeded - capacityIncrease < -workdayCapacityIncrease) {
              potentialSaturdays.unshift(dayData);
              break;
            }
            
            mockedAISuggestions.push({ date: format(dayData.date, 'yyyy-MM-dd'), newType: "AZUL_CLARO" });
            currentMonthCapacity += capacityIncrease;
            capacityNeeded -= capacityIncrease;
            extraDaysAddedThisMonth++;
          }
          
          // Usa dias úteis para ajuste fino
          potentialWorkdays.sort(() => Math.random() - 0.5);
          while (capacityNeeded > 0 && potentialWorkdays.length > 0 && extraDaysAddedThisMonth < MAX_EXTRA_DAYS_PER_MONTH_IA) {
            const dayData = potentialWorkdays.shift()!;
            const oldCapacityDay = getCapacityForDay(dayData);
            const newDayData = { ...dayData, color: DayColors.AZUL_ESCURO.background, type: DayColors.AZUL_ESCURO.meaning };
            const newCapacityDay = getCapacityForDay(newDayData);
            const capacityIncrease = newCapacityDay - oldCapacityDay;
            
            // Se este dia útil ultrapassar muito a demanda e ainda houver sábados disponíveis,
            // verifica se um sábado seria melhor
            if (capacityNeeded - capacityIncrease < 0 && potentialSaturdays.length > 0) {
              let bestSaturdayFit = null;
              let bestSaturdayDiff = Infinity;
              
              for (const satData of potentialSaturdays) {
                const satOldCapacity = getCapacityForDay(satData);
                const satNewCapacity = getCapacityForDay({...satData, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning});
                const satIncrease = satNewCapacity - satOldCapacity;
                const satDiff = Math.abs(capacityNeeded - satIncrease);
                
                if (satDiff < bestSaturdayDiff) {
                  bestSaturdayFit = satData;
                  bestSaturdayDiff = satDiff;
                }
              }
              
              if (bestSaturdayFit && bestSaturdayDiff < Math.abs(capacityNeeded - capacityIncrease)) {
                mockedAISuggestions.push({ date: format(bestSaturdayFit.date, 'yyyy-MM-dd'), newType: "AZUL_CLARO" });
                
                const satOldCapacity = getCapacityForDay(bestSaturdayFit);
                const satNewCapacity = getCapacityForDay({...bestSaturdayFit, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning});
                
                currentMonthCapacity += (satNewCapacity - satOldCapacity);
                capacityNeeded -= (satNewCapacity - satOldCapacity);
                extraDaysAddedThisMonth++;
                
                potentialSaturdays.splice(potentialSaturdays.indexOf(bestSaturdayFit), 1);
                potentialWorkdays.unshift(dayData);
                continue;
              }
            }
            
            mockedAISuggestions.push({ date: format(dayData.date, 'yyyy-MM-dd'), newType: "AZUL_ESCURO" });
            currentMonthCapacity += capacityIncrease;
            capacityNeeded -= capacityIncrease;
            extraDaysAddedThisMonth++;
          }
          
          // Se ainda precisar de capacidade e ainda houver sábados, usa-os
          while (capacityNeeded > 0 && potentialSaturdays.length > 0 && extraDaysAddedThisMonth < MAX_EXTRA_DAYS_PER_MONTH_IA) {
            const dayData = potentialSaturdays.shift()!;
            const oldCapacityDay = getCapacityForDay(dayData);
            const newDayData = { ...dayData, color: DayColors.AZUL_CLARO.background, type: DayColors.AZUL_CLARO.meaning };
            const newCapacityDay = getCapacityForDay(newDayData);
            
            mockedAISuggestions.push({ date: format(dayData.date, 'yyyy-MM-dd'), newType: "AZUL_CLARO" });
            currentMonthCapacity += (newCapacityDay - oldCapacityDay);
            capacityNeeded -= (newCapacityDay - oldCapacityDay);
            extraDaysAddedThisMonth++;
          }
        }
        // Cenário: Ocupação baixa (< TARGET_OCCUPATION) - APENAS PARA DEZEMBRO
        else if (stats.occupation < TARGET_OCCUPATION && stats.occupation !== Infinity && month === 11) {
          const decemberWorkdaysToFerias: DayData[] = [];
          
          // Coletar dias úteis de Dezembro, ordenados do fim para o início do mês
          for (let day = daysInMonth; day >= 1; day--) { 
            const currentDate = new Date(currentYear, month, day);
            const dateString = format(currentDate, 'yyyy-MM-dd');
            const dayData = calendarData[dateString] || getInitialDayData(currentDate);
            if (dayData.type === DayColors.PRETO.meaning && 
                !isSaturday(currentDate) && !isSunday(currentDate) &&
                dayData.type !== DayColors.VERMELHO.meaning && 
                dayData.type !== DayColors.MARROM.meaning) {
              decemberWorkdaysToFerias.push(dayData); 
            }
          }

          // Tentar alocar férias consecutivas no final de Dezembro
          let capacityToReduce = currentMonthCapacity - demand;
          
          for (const dayData of decemberWorkdaysToFerias) {
            if (capacityToReduce <= 0 || feriasDaysAddedThisMonth >= MAX_FERIAS_DAYS_PER_MONTH_IA) break;
            
            const oldCapacityDay = getCapacityForDay(dayData);
            const newDayData = { ...dayData, color: DayColors.ROSA.background, type: DayColors.ROSA.meaning };
            const newCapacityDay = getCapacityForDay(newDayData); 
            const capacityReduction = oldCapacityDay - newCapacityDay;

            // Verifica se a redução não será excessiva (mantém pelo menos 98% da demanda)
            if ((currentMonthCapacity - capacityReduction) >= demand * 0.98) { 
              mockedAISuggestions.push({ date: format(dayData.date, 'yyyy-MM-dd'), newType: "ROSA" });
              currentMonthCapacity -= capacityReduction;
              capacityToReduce -= capacityReduction;
              feriasDaysAddedThisMonth++;
            } else {
              break;
            }
          }
        }
      }

      if (mockedAISuggestions.length === 0) {
        console.log("Simulated AI: No changes suggested based on new mock logic.");
        alert("Sugestão IA (simulada): Nenhuma alteração foi sugerida com base na lógica de simulação atual.");
      } else {
        // Aplicar as sugestões
        const newCalendarData = { ...calendarData };
        let changesApplied = 0;
        mockedAISuggestions.forEach(suggestion => {
          const targetDate = parseISO(suggestion.date);
          const colorKey = suggestion.newType;
          // Verifica se a sugestão já não foi aplicada ou se o dia já não é do tipo sugerido
          const existingDayData = newCalendarData[suggestion.date];
          if (existingDayData && existingDayData.type === DayColors[colorKey].meaning) {
            return; // Já está no estado desejado
          }

          if (DayColors[colorKey]) {
            newCalendarData[suggestion.date] = {
              date: targetDate,
              color: DayColors[colorKey].background,
              type: DayColors[colorKey].meaning,
            };
            changesApplied++;
          } else {
            console.warn(`Invalid newType "${colorKey}" suggested by AI for date ${suggestion.date}.`);
          }
        });
        if (changesApplied > 0) {
          setCalendarData(newCalendarData);
          alert(`Sugestão IA (simulada): ${changesApplied} alterações aplicadas ao calendário.`);
        }
      }

    } catch (error) {
      console.error("Error processing simulated AI suggestion:", error);
      alert("Ocorreu um erro ao processar a sugestão da IA (simulada).");
    }
  }, [calendarData, currentYear, monthlyStats, appParams.selectedOpenAIModel, appParams.aiPrompt, getInitialDayData, getCapacityForDay]);

  const updateMonthlyDemand = useCallback((monthKey: string, newDemand: number) => {
    setMonthlyStats(prevStats => {
      const currentMonthStats = prevStats[monthKey];
      if (currentMonthStats) {
        const newOccupation = currentMonthStats.capacity > 0 
          ? parseFloat(((newDemand / currentMonthStats.capacity) * 100).toFixed(1)) 
          : Infinity;
        return {
          ...prevStats,
          [monthKey]: {
            ...currentMonthStats,
            demand: newDemand,
            occupation: newOccupation,
          },
        };
      }
      return prevStats;
    });
  }, []);

  const resetCalendarState = useCallback(() => {
    const year = currentYear;
    const initialCalendarData: CalendarData = {};
    const initialMonthlyStats: Record<string, MonthlyStats> = {};

    for (let month = 0; month < 12; month++) {
      const daysInMonth = getDaysInMonth(new Date(year, month));
      let monthCapacity = 0;
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, month, day);
        const dateString = format(currentDate, 'yyyy-MM-dd');
        const dayData = getInitialDayData(currentDate);
        initialCalendarData[dateString] = dayData;
        monthCapacity += getCapacityForDay(dayData);
      }
      const monthKey = format(new Date(year, month), 'yyyy-MM');
      const demand = Math.floor(Math.random() * (60000 - 50000 + 1)) + 50000;
      const occupation = monthCapacity > 0 ? parseFloat(((demand / monthCapacity) * 100).toFixed(1)) : Infinity;
      initialMonthlyStats[monthKey] = { demand, capacity: monthCapacity, occupation };
    }

    setCalendarData(initialCalendarData);
    setMonthlyStats(initialMonthlyStats);
  }, [currentYear, getInitialDayData, getCapacityForDay]);

  const value = {
    currentYear,
    setCurrentYear,
    calendarData,
    updateDayColor,
    getInitialDayData,
    monthlyStats,
    updateMonthlyDemand,
    applyAutomaticSuggestions,
    applyOpenAISuggestions,
    appParams,
    updateAppParams,
    getCapacityForDay,
    getAIDefaultPrompt,
    resetCalendarState,
  };

  return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>;
};

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error("useCalendar must be used within a CalendarProvider");
  }
  return context;
};
